import { useEffect, useRef, useState, useCallback } from 'react';
import { App as AntdApp } from 'antd';
import {
  getActiveRecordId,
  getActiveTableContext,
  getFieldMetaList,
  getRecordIds,
  getTableName,
  getVisibleFieldIds,
  onContextChange,
  onRecordSelectionChange,
} from './api/bitable';
import type { FieldMeta, TableContext } from './api/types';
import { BitableApiError } from './api/types';
import { AppProvider } from './store/context';
import { useAppDispatch, useAppState } from './store/hooks';
import { Wordmark } from './components/shell/Wordmark';
import { UserIdChip } from './components/shell/UserIdChip';
import { ErrorBoundary } from './components/shell/ErrorBoundary';
import { BillingProvider } from './billing/context';
import { useBilling } from './billing/hooks';
import { HomePage } from './views/HomePage';
import { EditorPage } from './views/EditorPage';
import { TemplatesPage } from './views/TemplatesPage';
import { TemplateStorePage } from './views/TemplateStorePage';
import { PreviewPage } from './views/PreviewPage';
import { GROUP_LINK, openHelp } from './help-feedback.mount';
import { HELP_DOC_URL } from './components/shell/EditorGuide';
import { initCloudSync, pushTableMeta } from './store/cloudSync';

/** 底部常驻求助栏：随时能进用户群提问，不用找隐藏入口（对齐签字确认插件的做法）。 */
function SupportBar() {
  const link: React.CSSProperties = {
    color: 'var(--accent)',
    cursor: 'pointer',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  };
  return (
    <div
      style={{
        flexShrink: 0,
        borderTop: '1px solid var(--border)',
        background: 'var(--surface-1)',
        padding: '7px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        color: 'var(--ink-3)',
      }}
    >
      <span>遇到问题？</span>
      <span
        style={link}
        onClick={() => window.open(GROUP_LINK, '_blank', 'noopener,noreferrer')}
      >
        🔥 加入用户咨询群
      </span>
      <span style={{ flex: 1 }} />
      <span
        style={{ ...link, fontWeight: 500 }}
        onClick={() => window.open(HELP_DOC_URL, '_blank', 'noopener,noreferrer')}
      >
        帮助文档
      </span>
      <span style={{ ...link, fontWeight: 500 }} onClick={openHelp}>
        快速上手
      </span>
    </div>
  );
}

interface BootstrapState {
  loading: boolean;
  fieldMetas: FieldMeta[];
  visibleFieldIds: string[];
  tableName: string | null;
  error: string | null;
}

const INITIAL: BootstrapState = {
  loading: true,
  fieldMetas: [],
  visibleFieldIds: [],
  tableName: null,
  error: null,
};

function describeError(err: unknown): string {
  if (err instanceof BitableApiError) return `[${err.code}] ${err.message}`;
  if (err instanceof Error) return err.message;
  return String(err);
}

function Header() {
  const { access, accountId, openBuy } = useBilling();
  const pro = access?.pro === true;
  return (
    <header
      style={{
        // 侧边栏是窄长条，首屏很贵：品牌条 + 数据表状态 + 模式切换三层叠起来
        // 原本要吃掉近 1/5 首屏。这里收紧上下内边距。
        padding: '9px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        borderBottom: '1px solid var(--border-soft)',
        flexShrink: 0,
        background: 'var(--surface-1)',
      }}
    >
      <Wordmark />
      <div style={{ flex: 1 }} />
      <UserIdChip userId={accountId} />
      <span
        style={{
          fontSize: 11,
          color: 'var(--ink-3)',
          whiteSpace: 'nowrap',
        }}
      >
        {/* 原来这里硬编码 #e8732a，跟主题 --accent (#bc5a32) 不是一个橙色 */}
        {pro ? (
          <b style={{ color: 'var(--accent)' }}>{access?.viaTeam ? '团队版' : '高级版'}</b>
        ) : (
          <>
            免费版{access ? ` · 本月剩余 ${access.freeRemaining}/${access.freeLimit} 次` : ''}
          </>
        )}
      </span>
      {!pro && (
        <button
          onClick={openBuy}
          style={{
            height: 24,
            padding: '0 12px',
            borderRadius: 6,
            border: 0,
            // 描边红字在纸感底上对比度太低，不像可点的行动点；改成实心
            background: 'var(--accent)',
            color: 'var(--accent-ink)',
            fontSize: 11.5,
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          购买
        </button>
      )}
    </header>
  );
}

function PluginShell() {
  const state = useAppState();
  const dispatch = useAppDispatch();
  const [bootstrap, setBootstrap] = useState<BootstrapState>(INITIAL);

  // 切表、刷新、首次加载可能并发。没有令牌的话，慢的那次会最后落地 ——
  // 于是 A 表的字段配上 B 表的上下文，预览要么空白要么报错，直到再切一次表。
  const loadTokenRef = useRef(0);
  const load = useCallback(
    async (ctx?: TableContext) => {
      const token = ++loadTokenRef.current;
      setBootstrap((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const context = ctx ?? (await getActiveTableContext());
        if (token !== loadTokenRef.current) return;
        dispatch({ type: 'SET_CONTEXT', context });
        const [metas, visibleFieldIds, tableName, recordId] = await Promise.all([
          getFieldMetaList(context.tableId),
          getVisibleFieldIds(context.tableId, context.viewId),
          getTableName(context.tableId).catch(() => null),
          getActiveRecordId().catch(() => null),
        ]);
        if (token !== loadTokenRef.current) return;
        dispatch({ type: 'LOAD_FIELDS', metas });
        // 用户还没点过任何行时，默认拿视图第一条兜底 —— 否则预览一直空着，
        // 看上去像"我填了内容但没渲染"。
        const fallbackId =
          recordId ??
          (await getRecordIds(context.tableId, context.viewId, 'visible')
            .then((ids) => ids[0] ?? null)
            .catch(() => null));
        if (token !== loadTokenRef.current) return;
        dispatch({ type: 'SET_CONTRACT_RECORD_ID', recordId: fallbackId });
        setBootstrap({
          loading: false,
          fieldMetas: metas,
          visibleFieldIds,
          tableName,
          error: null,
        });
        // 把字段快照推给渲染服务：自动化节点只能按字段名传值，服务端要靠这张
        // 对照表把名称换回真实 fieldId，否则模板里按 id 绑定的区块会渲染成空。
        void pushTableMeta(context.tableId, metas);
      } catch (err) {
        if (token !== loadTokenRef.current) return;
        setBootstrap({
          loading: false,
          fieldMetas: [],
          visibleFieldIds: [],
          tableName: null,
          error: describeError(err),
        });
      }
    },
    [dispatch],
  );

  // 存储写失败、模板到上限：这两件事以前都是静默的，客户的表现是「模板又丢了」
  // 或者「建了没反应」。统一在这里弹一次提示。
  const { notification } = AntdApp.useApp();
  useEffect(() => {
    const onStorageFailed = () => {
      notification.error({
        key: 'pp-storage-failed',
        message: '模板没能保存到本地',
        description:
          '浏览器存储空间已满，本次改动只存在于当前页面，刷新后会丢失。常见原因是印章/水印图太大，请压缩图片或删掉不用的模板后重试。',
        duration: 0,
      });
    };
    const onLimit = () => {
      notification.warning({
        key: 'pp-template-limit',
        message: '这张表的模板数量已达上限',
        description: '请先删除不再使用的模板，再新建。',
      });
    };
    window.addEventListener('pp-storage-failed', onStorageFailed);
    window.addEventListener('pp-template-limit', onLimit);
    return () => {
      window.removeEventListener('pp-storage-failed', onStorageFailed);
      window.removeEventListener('pp-template-limit', onLimit);
    };
  }, [notification]);

  useEffect(() => {
    void load();
    void initCloudSync(); // 模板云同步：拉云端合并 + 后续写入防抖推送
    const offContext = onContextChange((ctx) => {
      void load(ctx);
    });
    const offRecord = onRecordSelectionChange((recordId) => {
      // 点表头/点空白会推 null，不能因此把预览清空 —— 保留上一次选中的记录
      if (!recordId) return;
      dispatch({ type: 'SET_CONTRACT_RECORD_ID', recordId });
    });
    return () => {
      offContext();
      offRecord();
    };
  }, [load, dispatch]);

  // Whether to show the persistent header above the view.
  // Editor / Templates / Preview have their own header bars, so we keep
  // the plugin wordmark only on Home.
  const showHeader = state.view === 'home';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface-1)',
        color: 'var(--ink)',
      }}
    >
      {showHeader && <Header />}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* 渲染出错时别把整个侧边栏变成白屏 —— 至少让用户能退回首页换模板。
            key 绑视图：换个视图就重置错误态，不用重开插件。 */}
        <ErrorBoundary
          key={state.view}
          onReset={() => dispatch({ type: 'NAV', view: 'home', editingTemplateId: null })}
        >
        {state.view === 'home' && (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
            className="pp-scroll"
          >
            <HomePage
              fieldMetas={bootstrap.fieldMetas}
              tableName={bootstrap.tableName}
              bootstrapError={bootstrap.error}
              bootstrapLoading={bootstrap.loading}
              visibleFieldIds={bootstrap.visibleFieldIds}
              onRefresh={() => void load()}
            />
          </div>
        )}
        {state.view === 'editor' && (
          <EditorPage
            fieldMetas={bootstrap.fieldMetas}
            visibleFieldIds={bootstrap.visibleFieldIds}
            bootstrapLoading={bootstrap.loading}
          />
        )}
        {state.view === 'templates' && <TemplatesPage />}
        {state.view === 'store' && <TemplateStorePage fieldMetas={bootstrap.fieldMetas} />}
        {state.view === 'preview' && <PreviewPage fieldMetas={bootstrap.fieldMetas} />}
        </ErrorBoundary>
      </div>

      <SupportBar />
    </div>
  );
}

export default function App() {
  return (
    <AppProvider>
      <AntdApp>
        <BillingProvider>
          <PluginShell />
        </BillingProvider>
      </AntdApp>
    </AppProvider>
  );
}
